import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatOption, provideNativeDateAdapter } from '@angular/material/core';
import { ApiService } from '../../shared/services/api.service';
import { MaterialModule } from '../../shared/theming/material/material.module';
import { take } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { Conversation } from '../../shared/models/conversation.model';
import { DisplayChatComponent } from '../dashboard/components/display-chat/display-chat.component';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { PageToolbarComponent } from '../../shared/components/page-toolbar/page-toolbar.component';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { TriageConversationDialogComponent } from '../dashboard/components/triage-conversation-dialog/triage-conversation-dialog.component';
import { Router } from '@angular/router';
import { PlanNumberRevealService } from '../../shared/services/plan-number-reveal.service';
import { LogExporterComponent } from '../log-exporter/components/log-exporter.component';

@Component({
  selector: 'app-conversation-history',
  standalone: true,
  imports: [
    MaterialModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatOption,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    LogExporterComponent,
    PageToolbarComponent,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './conversation-history.component.html',
  styleUrls: ['./conversation-history.component.scss'],
})
export class ConversationHistoryComponent implements OnInit {
  private apiService = inject(ApiService);
  private fb = inject(FormBuilder);
  private _bottomSheet = inject(MatBottomSheet);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private planNumberRevealService = inject(PlanNumberRevealService);

  planNumberForm: FormGroup;
  startDate: Date | null = null;
  endDate: Date | null = null;
  isExporting: boolean = false;

  isLoadingResults = false;
  isRateLimitReached = false;

  conversations = new MatTableDataSource<Conversation>([]);

  resultsLength = 0;
  currentPage = 0;

  lastEvaluatedKey: string | null = null;

  displayedColumns: string[] = [
    'planNumber',
    'productType',
    'sessionId',
    'pk',
    'sk',
    'messageHistory',
    'actions',
  ];

  solutionType: string = 'all';
  solutionTypeOptions = [
    { value: 'all', label: 'All' },
    { value: 'optimized-rag', label: 'Optimized RAG' },
    { value: 'rag', label: 'RAG' },
    { value: 'agent-llm', label: 'Agent LLM' },
  ];

  ratingScore: string = 'all';
  ratingScoreOptions = [
    { value: 'all', label: 'All' },
    { value: '5', label: '5' },
    { value: '4', label: '4' },
    { value: '3', label: '3' },
    { value: '2', label: '2' },
    { value: '1', label: '1' },
  ];

  ratingControl: string = '=';
  ratingControlOptions = [
    { value: '=', label: 'Equal' },
    // { value: '>', label: 'Greater' },
    // { value: '<', label: 'Lower' },
  ];

  isPlanNumberRevealed = false;

  constructor() {
    this.planNumberForm = this.fb.group({
      planNumber: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.isPlanNumberRevealed =
      this.planNumberRevealService.getIsPlanNumberRevealed();

    // Just fetches all of this
    this.fetchBedrockConversations();
  }

  backToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  private buildConversationFilters(): {
    startDate?: string;
    endDate?: string;
    solutionType?: string;
    ratingScore?: string;
    ratingControl?: string;
  } {
    const filters: {
      startDate?: string;
      endDate?: string;
      solutionType?: string;
      ratingScore?: string;
      ratingControl?: string;
    } = {};

    if (this.startDate && this.endDate) {
      filters.startDate = this.startDate.toISOString();
      filters.endDate = this.endDate.toISOString();
    }

    if (this.solutionType !== 'all') {
      filters.solutionType = this.solutionType;
    }

    if (this.ratingScore !== 'all') {
      filters.ratingScore = this.ratingScore;
      filters.ratingControl = this.ratingControl;
    }

    return filters;
  }

  fetchConversations(actionType: 'export' | 'fetch') {
    if (!this.startDate || !this.endDate) {
      console.error('Please select both start and end dates.');
      this.isExporting = false;
      return;
    }
    const filters = this.buildConversationFilters();
    const payload: {
      startDate: string;
      endDate: string;
      solutionType?: string;
      ratingScore?: string;
      ratingControl?: string;
    } = {
      startDate: this.startDate.toISOString(),
      endDate: this.endDate.toISOString(),
      solutionType: filters.solutionType,
      ratingScore: filters.ratingScore,
      ratingControl: filters.ratingControl,
    };

    if (actionType === 'export') {
      this.isExporting = true;
      this.exportLogs(payload);
    }

    this.fetchBedrockConversations();
  }

  exportLogs(payload: any) {
    this.apiService
      .postCsv<
        any,
        {
          startDate: string;
          endDate: string;
          solutionType?: string;
          ratingScore?: string;
          ratingControl?: string;
        }
      >('admin/export-bedrock-conversation-logs', payload)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const blob = new Blob([response as any], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'bedrock-conversation-logs.csv';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          this.isExporting = false;
        },
        error: (err) => {
          console.error('Export failed:', err);
          this.isExporting = false;
          alert(
            'Export failed. Please try again later, or reduce the selected date range.'
          );
        },
      });
  }

  searchByPlanNumber() {
    const planNumber = this.planNumberForm.value.planNumber;
    if (!planNumber) {
      console.error('Plan number is required');
      return;
    }

    this.apiService
      .post<
        { data: Conversation[]; totalCount: number },
        { planNumber: string }
      >('admin/get-conversations-by-plan-number', { planNumber })
      .pipe(take(1))
      .subscribe(
        (response) => {
          console.log('Response:', response);
          this.conversations = new MatTableDataSource<Conversation>(
            response.data
          );
          this.resultsLength = response.totalCount;
        },
        (error) => {
          console.error('Error fetching conversations:', error);
        }
      );
  }

  viewConversation(conversation: Conversation) {
    this._bottomSheet.open(DisplayChatComponent, {
      data: {
        messageHistory: conversation.messageHistory,
      },
    });
  }

  viewTriageConversation(conversation: Conversation) {
    this.apiService
      .post<
        {
          data: {
            triageSessionId: string;
            triageData: any;
            chatHistory: any[];
            totalMessages: number;
          };
        },
        { sessionId: string; pk: string }
      >('admin/get-conversation-by-triage-session', {
        sessionId:
          conversation.triageQuestionSessionId ?? conversation.sessionId,
        pk: conversation.pk,
      })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.dialog.open(TriageConversationDialogComponent, {
            width: '700px',
            maxHeight: '85vh',
            data: response.data,
          });
        },
        error: (err) => {
          console.error('Error fetching triage conversation:', err);
        },
      });
  }

  removePlanNumber(): void {
    this.planNumberForm.patchValue({ planNumber: '' });
    this.planNumberForm.markAsPristine();
    this.planNumberForm.markAsUntouched();

    this.fetchBedrockConversations();
  }

  fetchBedrockConversations(event?: PageEvent) {
    const pageSize = event?.pageSize || 10;
    const queryParams: Record<string, string> = {
      ...this.buildConversationFilters(),
      limit: String(pageSize),
    };

    if (this.lastEvaluatedKey) {
      queryParams['lastEvaluatedKey'] = this.lastEvaluatedKey;
    }

    this.isLoadingResults = true;

    this.apiService
      .get<{
        data: Conversation[];
        lastEvaluatedKey: string;
        totalCount: number;
      }>('admin/get-bedrock-conversations', queryParams)
      .subscribe((response) => {
        this.conversations = new MatTableDataSource<Conversation>(
          response.data
        );
        this.isLoadingResults = false;
        this.lastEvaluatedKey = response.lastEvaluatedKey;
        this.resultsLength = response.totalCount;
      });
  }
}
